import { WebDashboardTrialUserRecord } from "@coeff/api";
import moment from "moment";

// Mirrors product_billing_utils.WEB_DASHBOARD_TRIAL_DAYS — the countdown a user with no trial
// would organically get on their first dashboard build.
const DEFAULT_TRIAL_DAYS = 30;

// Floor a new expiry must clear so a bulk update never shortens what a selected user effectively
// has. A user with a real expiry (own or domain-inherited) contributes that date; a user with no
// trial at all contributes an implicit 30-day-from-now floor (what they'd get organically); a user
// with a trial but no end date (e.g. unlimited) has nothing to shorten and is ignored, as are
// unselected users. Returns null when no selected user has any floor to protect.
export const getSelectedUsersMaxEndDt = (
  users: WebDashboardTrialUserRecord[],
  selectedEmails: string[]
): moment.Moment | null =>
  users
    .filter(u => selectedEmails.includes(u.email))
    .reduce<moment.Moment | null>((max, u) => {
      const end = u.trial_state_end_dt
        ? moment(u.trial_state_end_dt)
        : u.trial_state
        ? null
        : moment().add(DEFAULT_TRIAL_DAYS, "days");
      if (end === null) {
        return max;
      }
      return max === null || end.isAfter(max) ? end : max;
    }, null);

// A new manual-countdown expiry is valid only when it is set and strictly after
// the current expiry of the selected users (any future date is fine when none
// of them currently have an expiry). Admins behind the trial-admin-overrides gate
// may shorten a trial — for them the only floor is "in the future".
export const isExpirationValid = (
  expirationDate: moment.Moment | null,
  currentMaxEndDt: moment.Moment | null,
  allowShortening: boolean
): boolean => {
  if (expirationDate === null) {
    return false;
  }
  if (allowShortening) {
    return expirationDate.isAfter(moment());
  }
  return currentMaxEndDt === null || expirationDate.isAfter(currentMaxEndDt);
};
